import React, { memo, useCallback, useState } from 'react';
import EditJackpotModal from './editJackpotModal';
import { JackpotConfigUpdate } from 'apis/adminServices/jackpotServices';
import { JackpotConfigAPI } from './JackpotSetting';
import { AdminTable, StatusBadge, IconButton } from 'components/admin';
import { ColumnDef } from 'types/admin';
import ConfirmDeleteModal from '../DeleteModel';

interface JackpotTableProps {
  jackpots: JackpotConfigAPI[];
  onDelete: (id: number[], configName: string) => Promise<void> | void;
  loading: boolean;
  onUpdate?: (updatedJackpot: JackpotConfigAPI) => void;
  refreshTable?: () => void;
}

const JackpotTable: React.FC<JackpotTableProps> = memo(({ jackpots, onDelete, loading, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJackpot, setSelectedJackpot] = useState<JackpotConfigAPI | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [jackpotToDelete, setJackpotToDelete] = useState<{ id: number[]; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openModal = useCallback((jackpot: JackpotConfigAPI) => {
    setSelectedJackpot(jackpot);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedJackpot(null);
  }, []);

  const handleUpdate = useCallback((updatedJackpot: JackpotConfigUpdate) => {
    if (!selectedJackpot) return;

    const localUpdatedJackpot: JackpotConfigAPI = {
      configName: selectedJackpot.configName, // Assuming configName doesn't change in edit
      createdDate: selectedJackpot.createdDate,
      status: updatedJackpot.isActive ? 'Active' : 'Disabled',
      answer: updatedJackpot.answer,
      validFrom: updatedJackpot.validFrom.split('T')[0],
      validTo: updatedJackpot.validTo.split('T')[0],
      configurations: updatedJackpot.airlineId.map((aid, index) => ({
        jackpotId: updatedJackpot.jackpotId[0], // Use first jackpotId
        airlineId: aid,
      })),
    };
    onUpdate?.(localUpdatedJackpot);
    closeModal();
  }, [onUpdate, closeModal, selectedJackpot]);

  const toISODate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
  };

  // Define table columns
  const columns: ColumnDef<JackpotConfigAPI>[] = [
    {
      key: 'configName',
      header: 'Configuration Name',
    },
    {
      key: 'createdDate',
      header: 'Created Date',
    },
    {
      key: 'status',
      header: 'Status',
      render: (jackpot) => <StatusBadge status={jackpot.status} />,
    },
  ];

  // Custom actions renderer
  const renderActions = (jackpot: JackpotConfigAPI) => (
    <>
      <IconButton
        variant="edit"
        onClick={() => openModal(jackpot)}
                    title="Edit"
        ariaLabel="Edit jackpot configuration"
      />
      <IconButton
        variant="delete"
                    onClick={() => {
                      if (!jackpot.configurations?.[0]?.jackpotId) return;
                      setJackpotToDelete({ id: jackpot.configurations?.map(c => c.jackpotId), name: jackpot.configName });
                      setIsDeleteModalOpen(true);
                    }}
                    title="Delete"
        ariaLabel="Delete jackpot configuration"
        disabled={loading || isDeleting}
      />
    </>
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={jackpots}
        loading={loading}
        emptyMessage="No jackpot configurations found"
        renderActions={renderActions}
        showSerialNumber={true}
      />

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        configurationName={jackpotToDelete?.name || ''}
        onConfirm={async () => {
          if (!jackpotToDelete) return;
          try {
            setIsDeleting(true);
            await onDelete(jackpotToDelete.id, jackpotToDelete.name);
            setIsDeleteModalOpen(false);
            setJackpotToDelete(null);
          } catch (error) {
            // Parent handles toast/logging; keep modal open for retry
            console.error('Jackpot deletion failed:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        onCancel={() => {
          if (isDeleting) return;
          setIsDeleteModalOpen(false);
          setJackpotToDelete(null);
        }}
      />

      {selectedJackpot && (
        <EditJackpotModal
          isOpen={isModalOpen}
          jackpot={{
            jackpotId: selectedJackpot.configurations?.map(c => c.jackpotId) || [], 
            airlineId: selectedJackpot.configurations?.map(c => c.airlineId) || [],
            answer: selectedJackpot.answer || 0,
            validFrom: selectedJackpot.validFrom
              ? new Date(selectedJackpot.validFrom).toISOString()
              : toISODate(selectedJackpot.createdDate),
            validTo: selectedJackpot.validTo
              ? new Date(selectedJackpot.validTo).toISOString()
              : toISODate(selectedJackpot.createdDate),
            isActive: selectedJackpot.status === 'Active',
            configurationName: selectedJackpot.configName,
            configurations: selectedJackpot.configurations,
          }}
          onClose={closeModal}
          onUpdate={handleUpdate}
        />
      )}
    </>
  );
});

JackpotTable.displayName = 'JackpotTable';

export default JackpotTable;
